import React, { type FC, memo, useCallback, useMemo, useState } from "react";
import styles from "./DocumentThumbnail.module.css";
import { Button, cn, type PressEvent, Spinner, Tooltip } from "@heroui/react";
import { useDmsDocument } from "@domain/context/DmsDataContexts";
import { FormattedMessage, useIntl } from "react-intl";
import { Copy, TrashBin } from "@gravity-ui/icons";
import { PopconfirmHero } from "@ui-components/PopconfirmHero";
import { writeToClipboard } from "@domain/utils/copyToClipboard";

const CONFIRMATION_TITLE = <FormattedMessage id="app.confirmation.header" />;

type TProps = {
    fileId?: string;
    title?: string;
    onDelete?: () => void;
    onAccept?: (file: File[]) => void;
    onClick?: () => void;
    className?: string;
    selected?: boolean;
    urlFallback?: string;
    position?: number;
    primary?: PrimaryBadge;
};

// Only a record sends documents to DATEV (an e-invoice alone, else position 1 glued with the rest); other entities get the generic label.
export type PrimaryBadge = "datev" | "eInvoice" | "generic";

const PRIMARY_MESSAGE_IDS: Record<PrimaryBadge, { label: string; hint: string }> = {
    datev: { label: "app.dms.datevBeleg", hint: "app.dms.datevBeleglinkHint" },
    eInvoice: { label: "app.dms.eInvoiceBeleg", hint: "app.dms.eInvoiceBelegHint" },
    generic: { label: "app.dms.primaryBeleg", hint: "app.dms.primaryBelegHint" },
};

const DocumentOrderBadge: FC<Pick<TProps, "position" | "primary">> = ({ position, primary }) => {
    const intl = useIntl();

    if (!position) {
        return null;
    }

    return (
        <div className="absolute left-1 top-1 z-10 flex items-center gap-1">
            <span
                className="flex h-5 min-w-5 items-center justify-center rounded-full bg-default px-1 text-xs font-bold text-foreground shadow"
                aria-label={intl.formatMessage({ id: "app.dms.documentPosition" }, { position })}
            >
                {position}
            </span>
            {primary && (
                <span
                    className="rounded bg-accent px-1 py-0.5 text-[10px] font-bold text-white"
                    title={intl.formatMessage({ id: PRIMARY_MESSAGE_IDS[primary].hint })}
                >
                    <FormattedMessage id={PRIMARY_MESSAGE_IDS[primary].label} />
                </span>
            )}
        </div>
    );
};

export const DocumentThumbnail: FC<TProps> = memo(({ fileId, onClick, onDelete, selected, position, primary }) => {
    const [tooltipTitle, setTooltipTitle] = useState(<FormattedMessage id="app.dms.copyId" />);

    const previewUrl = useDmsDocument(fileId)?.previewUrl;

    const copyId = useCallback(
        async (_e: PressEvent) => {
            // e.stopPropagation();
            const ok = await writeToClipboard(fileId);
            setTooltipTitle(<FormattedMessage id={ok ? "app.dms.idCopied" : "app.dms.copyFailed"} />);
            setTimeout(() => {
                setTooltipTitle(<FormattedMessage id="app.dms.copyId" />);
            }, 3000);
        },
        [fileId],
    );

    const containerStyle = useMemo(() => ({ outline: selected ? "2px solid #1890ff" : undefined }), [selected]);
    const backgroundStyle = useMemo(() => ({ backgroundImage: `url(${previewUrl})` }), [previewUrl]);
    const deleteTrigger = useMemo(
        () => (
            <Button variant="tertiary" size="sm" isIconOnly isDisabled={!fileId}>
                <TrashBin className="text-red-500 size-4" />
            </Button>
        ),
        [fileId],
    );

    return (
        <div
            onClick={onClick}
            className={cn(
                "relative flex w-4/5 flex-col items-center rounded-lg mx-auto my-2.5 shadow-lg hover:cursor-pointer",
                styles.documentThumbnailContainer,
            )}
            style={containerStyle}
            key={fileId}
        >
            <DocumentOrderBadge position={position} primary={primary} />
            <div className="relative w-full pt-[141%]">
                {previewUrl ? (
                    <div
                        className="absolute inset-0 rounded-lg bg-center bg-cover"
                        style={backgroundStyle}
                        id={fileId}
                    />
                ) : (
                    <div className="absolute inset-0 rounded-lg bg-center bg-cover flex items-center justify-center">
                        <Spinner size="md" />
                    </div>
                )}
            </div>
            <div className="absolute inset-x-0 bottom-0 flex flex-wrap min-h-10 items-center justify-between p-2.5 rounded-b-lg bg-default">
                <Tooltip delay={0} closeDelay={0}>
                    <Tooltip.Trigger>
                        <Button variant="tertiary" onPress={copyId} size="sm" isIconOnly isDisabled={!fileId}>
                            <Copy className="size-4" />
                        </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                        <p>{tooltipTitle}</p>
                    </Tooltip.Content>
                </Tooltip>

                {onDelete ? (
                    <PopconfirmHero title={CONFIRMATION_TITLE} onConfirm={onDelete} trigger={deleteTrigger} />
                ) : null}
            </div>
        </div>
    );
});

export const DocumentIcon: FC<Pick<TProps, "onClick" | "fileId" | "selected" | "position" | "primary">> = memo(
    ({ onClick, fileId, selected, position, primary }) => {
        const intl = useIntl();
        const containerStyle = useMemo(() => ({ outline: selected ? "2px solid #1890ff" : undefined }), [selected]);
        return (
            <div
                onClick={onClick}
                className={cn(
                    "relative flex w-4/5 flex-col items-center rounded-lg mx-auto my-2.5 shadow-lg hover:cursor-pointer",
                    styles.documentThumbnailContainer,
                )}
                style={containerStyle}
                key={fileId}
            >
                <div className="relative w-full pt-[141%]">
                    {position ? (
                        <span
                            className="absolute inset-0 flex items-center justify-center text-xs font-bold text-foreground"
                            aria-label={intl.formatMessage({ id: "app.dms.documentPosition" }, { position })}
                        >
                            {position}
                        </span>
                    ) : null}
                    {primary && (
                        <span
                            className="absolute right-1 top-1 size-2 rounded-full bg-accent"
                            title={intl.formatMessage({ id: PRIMARY_MESSAGE_IDS[primary].hint })}
                        />
                    )}
                </div>
            </div>
        );
    },
);
